import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Calendar,
  Users,
  Mail,
  Inbox,
  MessageSquare,
  Phone,
  Users2,
  UserRoundCheck,
  ClockAlert,
  RefreshCcw,
  LogOut,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";
import { format } from "date-fns";

const fmt = (d) => (d ? format(new Date(d), "PPp") : "—");

const Section = ({ title, description, action, children, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-border/70 bg-card p-6 md:p-8">
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-5">
      <div>
        <h2 className="font-serif-display text-2xl md:text-3xl text-foreground">{title}</h2>
        {description && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const KPI = ({ label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-border/70 bg-card p-6">
    <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">{label}</div>
    <div className="mt-2 font-serif-display text-3xl text-foreground">{value}</div>
  </div>
);

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(false);

  const [events, setEvents] = useState([]);
  const [parents, setParents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [emails, setEmails] = useState([]);
  const [chats, setChats] = useState([]);
  const [runs, setRuns] = useState([]);
  const [smsLog, setSmsLog] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [preview, setPreview] = useState(null);

  const loadAll = async () => {
    try {
      const [ev, pa, bk, iq, em, ch, sr, sm, ap] = await Promise.all([
        api.get("/admin/events"),
        api.get("/admin/parents"),
        api.get("/admin/bookings"),
        api.get("/inquiries"),
        api.get("/admin/email-log?limit=100"),
        api.get("/chat"),
        api.get("/admin/scheduler/last-runs"),
        api.get("/admin/sms-log?limit=100"),
        api.get("/admin/applicants"),
      ]);
      setEvents(ev.data);
      setParents(pa.data);
      setBookings(bk.data);
      setInquiries(iq.data);
      setEmails(em.data);
      setChats(ch.data);
      setRuns(sr.data);
      setSmsLog(sm.data);
      setApplicants(ap.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load data");
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runScheduler = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/scheduler/run-now");
      toast.success(
        `Job ran: ${data.emails_sent} sent, ${data.skipped_duplicates_or_missing} skipped`
      );
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Job failed");
    } finally {
      setBusy(false);
    }
  };

  const runSms = async (force = false) => {
    setBusy(true);
    try {
      const url = force ? "/admin/scheduler/run-sms?hours=0" : "/admin/scheduler/run-sms";
      const { data } = await api.post(url);
      toast.success(
        `SMS job (${data.provider}): ${data.sms_sent} sent, ${data.skipped_duplicate} dup, ${data.skipped_profile_complete} already complete`
      );
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "SMS job failed");
    } finally {
      setBusy(false);
    }
  };

  const openApplicant = async (id) => {
    try {
      const { data } = await api.get(`/admin/applicants/${id}`);
      setSelectedApplicant(data);
    } catch (e) {
      toast.error("Failed to load applicant");
    }
  };

  const updateApplicantStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applicants/${id}/status`, { status });
      toast.success(`Status updated: ${status}`);
      await loadAll();
      await openApplicant(id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update");
    }
  };

  const revealSSN = async (id) => {
    try {
      const { data } = await api.get(`/admin/applicants/${id}/ssn`);
      toast.success(`SSN: ${data.ssn}`, { duration: 8000 });
    } catch (e) {
      toast.error("Could not reveal SSN");
    }
  };

  const downloadFile = async (applicantId, fileId, kind) => {
    try {
      const resp = await api.get(`/admin/applicants/${applicantId}/files/${fileId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}-${fileId}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const openPreview = async (id) => {
    try {
      const { data } = await api.get(`/admin/email-log/${id}`);
      setPreview(data);
    } catch (e) {
      toast.error("Failed to load email");
    }
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-muted/40">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-serif-display">C</span>
            <span className="font-serif-display text-lg">Care Anywhere · Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-muted-foreground">
              {user?.email}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={runScheduler}
              disabled={busy}
              data-testid="btn-run-scheduler"
              className="rounded-full"
            >
              <RefreshCcw size={14} className="mr-1.5" />
              {busy ? "Running…" : "Run pre-event job"}
            </Button>
            <Button
              asChild
              size="sm"
              data-testid="btn-open-checkin"
              className="rounded-full bg-primary text-primary-foreground"
            >
              <Link to="/admin/checkin">
                <UserRoundCheck size={14} className="mr-1.5" /> Check-In Kiosk
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid="btn-logout"
              onClick={async () => { await logout(); nav("/admin/login"); }}
              className="rounded-full"
            >
              <LogOut size={14} className="mr-1.5" /> Sign out
            </Button>
            {/* alias testids for QA */}
            <button type="button" data-testid="run-scheduler-btn" onClick={runScheduler} className="sr-only" aria-hidden="true" tabIndex={-1}>run</button>
            <button type="button" data-testid="admin-logout" onClick={async () => { await logout(); nav("/admin/login"); }} className="sr-only" aria-hidden="true" tabIndex={-1}>logout</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <KPI testid="kpi-events" label="Events" value={events.length} />
          <KPI testid="kpi-parents" label="Parents" value={parents.length} />
          <KPI testid="kpi-bookings" label="Bookings" value={bookings.length} />
          <KPI testid="kpi-emails" label="Emails Sent" value={emails.length} />
          <KPI testid="kpi-sms" label="SMS Sent" value={smsLog.length} />
          <KPI testid="kpi-applicants" label="Applicants" value={applicants.length} />
        </div>
        {/* Aliased stat-* testids for backwards-compatible QA selectors */}
        <div className="sr-only" aria-hidden="true">
          <span data-testid="stat-inquiries">{inquiries.length}</span>
          <span data-testid="stat-events">{events.length}</span>
          <span data-testid="stat-parents">{parents.length}</span>
          <span data-testid="stat-emails">{emails.length}</span>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="mb-6 flex flex-wrap gap-1 bg-background rounded-full p-1 border border-border h-auto">
            <TabsTrigger data-testid="tab-overview" value="overview" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Calendar size={14} className="mr-1.5" />Overview</TabsTrigger>
            <TabsTrigger data-testid="tab-events" value="events" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Calendar size={14} className="mr-1.5" />Events</TabsTrigger>
            <TabsTrigger data-testid="tab-parents" value="parents" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Users size={14} className="mr-1.5" />Parents</TabsTrigger>
            <TabsTrigger data-testid="tab-bookings" value="bookings" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Calendar size={14} className="mr-1.5" />Bookings</TabsTrigger>
            <TabsTrigger data-testid="tab-inquiries" value="inquiries" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Inbox size={14} className="mr-1.5" />Inquiries</TabsTrigger>
            <TabsTrigger data-testid="tab-emails" value="emails" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Mail size={14} className="mr-1.5" />Emails</TabsTrigger>
            <TabsTrigger data-testid="tab-chat" value="chat" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><MessageSquare size={14} className="mr-1.5" />Chat</TabsTrigger>
            <TabsTrigger data-testid="tab-sms" value="sms" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Phone size={14} className="mr-1.5" />SMS</TabsTrigger>
            <TabsTrigger data-testid="tab-applicants" value="applicants" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Users2 size={14} className="mr-1.5" />Applicants</TabsTrigger>
            <TabsTrigger data-testid="tab-guests" value="guests" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Mail size={14} className="mr-1.5" />Guest emails</TabsTrigger>
            <TabsTrigger data-testid="tab-payments" value="payments" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Users size={14} className="mr-1.5" />Payments</TabsTrigger>
            <TabsTrigger data-testid="tab-settings" value="settings" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><ShieldCheck size={14} className="mr-1.5" />Settings</TabsTrigger>
            <TabsTrigger data-testid="tab-scheduler" value="scheduler" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><ClockAlert size={14} className="mr-1.5" />Scheduler</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Section title="Upcoming events" description="Any event starting within the 48h window will trigger the pre-event pipeline.">
              {events.length === 0 ? (
                <Empty>No events yet.</Empty>
              ) : (
                <div className="space-y-3">
                  {events.slice(0, 5).map((e) => (
                    <div key={e.id} className="flex items-center justify-between p-4 rounded-xl bg-muted/50 border border-border/50">
                      <div>
                        <div className="font-medium text-foreground">{e.name}</div>
                        <div className="text-xs text-muted-foreground">{e.slug} · {e.venue}</div>
                      </div>
                      <div className="text-sm text-muted-foreground">{fmt(e.start_at)}</div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </TabsContent>

          <TabsContent value="events">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Section title="All events" testid="section-events">
                  <Table>
                    <THead>
                      <TR><TH>Name</TH><TH>Slug</TH><TH>Venue</TH><TH>Starts</TH></TR>
                    </THead>
                    <TBody>
                      {events.map((e) => (
                        <TR key={e.id} testid={`event-row-${e.slug}`}>
                          <TD className="font-medium text-foreground">{e.name}</TD>
                          <TD><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{e.slug}</code></TD>
                          <TD>{e.venue}</TD>
                          <TD>{fmt(e.start_at)}</TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                </Section>
              </div>
              <div className="lg:col-span-1">
                <CreateEventForm onCreated={loadAll} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="bookings">
            <Section title="Bookings" description="All confirmed bookings across events." testid="section-bookings">
              <Table>
                <THead>
                  <TR><TH>Created</TH><TH>Event</TH><TH>Parent</TH><TH>Children</TH><TH>Status</TH></TR>
                </THead>
                <TBody>
                  {bookings.map((b) => {
                    const ev = events.find((e) => e.id === b.event_id);
                    const p = parents.find((pp) => pp.id === b.parent_id);
                    return (
                      <TR key={b.id} testid={`booking-row-${b.id}`}>
                        <TD>{fmt(b.created_at)}</TD>
                        <TD className="font-medium text-foreground">{ev?.name || b.event_id}</TD>
                        <TD>{p ? `${p.first_name} ${p.last_name}` : b.parent_id}</TD>
                        <TD>{(b.child_ids || []).length}</TD>
                        <TD><Badge variant={b.status === "confirmed" ? "default" : "secondary"}>{b.status}</Badge></TD>
                      </TR>
                    );
                  })}
                  {bookings.length === 0 && <TR><TD colSpan={5}><Empty>No bookings yet.</Empty></TD></TR>}
                </TBody>
              </Table>
            </Section>
          </TabsContent>

          <TabsContent value="parents">
            <Section title="Parents" description="Profile completeness drives which email variant fires.">
              <Table>
                <THead>
                  <TR><TH>Name</TH><TH>Email</TH><TH>Phone</TH><TH>Photo</TH><TH>Added</TH></TR>
                </THead>
                <TBody>
                  {parents.map((p) => (
                    <TR key={p.id} testid={`parent-row-${p.email}`}>
                      <TD className="font-medium text-foreground">{p.first_name} {p.last_name}</TD>
                      <TD>{p.email}</TD>
                      <TD>{p.phone}</TD>
                      <TD>{p.photo_url ? <Badge>uploaded</Badge> : <Badge variant="secondary">missing</Badge>}</TD>
                      <TD>{fmt(p.created_at)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </Section>
          </TabsContent>

          <TabsContent value="inquiries">
            <Section title="Landing form inquiries">
              <Table>
                <THead>
                  <TR><TH>Received</TH><TH>Name</TH><TH>Email</TH><TH>Service</TH><TH>Event Date</TH><TH>Location</TH></TR>
                </THead>
                <TBody>
                  {inquiries.map((i) => (
                    <TR key={i.id} testid={`inquiry-row-${i.email}`}>
                      <TD>{fmt(i.created_at)}</TD>
                      <TD className="font-medium text-foreground">{i.full_name}</TD>
                      <TD>{i.email}</TD>
                      <TD><Badge variant="secondary">{i.service_type}</Badge></TD>
                      <TD>{i.event_date}</TD>
                      <TD>{i.location}</TD>
                    </TR>
                  ))}
                  {inquiries.length === 0 && <TR><TD colSpan={6}><Empty>No inquiries yet.</Empty></TD></TR>}
                </TBody>
              </Table>
            </Section>
          </TabsContent>

          <TabsContent value="emails">
            <Section title="Email log" description="Every mocked send is stored here. Click a row to preview the rendered HTML." testid="section-emails">
              <Table>
                <THead>
                  <TR><TH>Sent</TH><TH>To</TH><TH>Variant</TH><TH>Subject</TH><TH></TH></TR>
                </THead>
                <TBody>
                  {emails.map((e) => (
                    <TR key={e.id} testid={`email-row-${e.id}`} onClick={() => openPreview(e.id)} className="cursor-pointer hover:bg-muted/50">
                      <TD>{fmt(e.sent_at)}</TD>
                      <TD>{e.to_email}</TD>
                      <TD>
                        <Badge variant={e.variant === "express_pass" ? "default" : "secondary"}>
                          {(e.variant || (e.tags && e.tags.kind) || "system").replace(/_/g, " ")}
                        </Badge>
                      </TD>
                      <TD className="max-w-[420px] truncate">{e.subject}</TD>
                      <TD><Button size="sm" variant="ghost" data-testid={`preview-email-${e.id}`}>Preview <ExternalLink size={12} className="ml-1" /></Button></TD>
                    </TR>
                  ))}
                  {emails.length === 0 && <TR><TD colSpan={5}><Empty>No emails yet — run the scheduler.</Empty></TD></TR>}
                </TBody>
              </Table>
            </Section>
          </TabsContent>

          <TabsContent value="chat">
            <Section title="Concierge messages">
              <Table>
                <THead>
                  <TR><TH>Received</TH><TH>From</TH><TH>Email</TH><TH>Message</TH></TR>
                </THead>
                <TBody>
                  {chats.map((c) => (
                    <TR key={c.id} testid={`chat-row-${c.id}`}>
                      <TD>{fmt(c.created_at)}</TD>
                      <TD className="font-medium text-foreground">{c.name}</TD>
                      <TD>{c.email}</TD>
                      <TD className="max-w-[520px]">{c.message}</TD>
                    </TR>
                  ))}
                  {chats.length === 0 && <TR><TD colSpan={4}><Empty>No concierge messages yet.</Empty></TD></TR>}
                </TBody>
              </Table>
            </Section>
          </TabsContent>

          <TabsContent value="sms">
            <Section
              title="SMS reminders"
              description="Fires 12h after the pre-event email if the parent's profile is still incomplete. Reuses the same magic link."
              testid="section-sms"
              action={
                <div className="flex gap-2">
                  <Button onClick={() => runSms(false)} disabled={busy} variant="outline" className="rounded-full" data-testid="btn-run-sms">
                    <RefreshCcw size={14} className="mr-1.5" />
                    {busy ? "Running…" : "Run reminder job"}
                  </Button>
                  <Button onClick={() => runSms(true)} disabled={busy} className="rounded-full bg-primary text-primary-foreground" data-testid="btn-run-sms-force">
                    <RefreshCcw size={14} className="mr-1.5" />
                    Force send now
                  </Button>
                </div>
              }
            >
              <div className="mb-4 text-xs text-muted-foreground">
                Provider:{" "}
                <Badge variant="secondary" data-testid="sms-provider-status">
                  {smsLog[0]?.provider === "twilio" ? "Twilio (LIVE)" : "Mocked — set TWILIO_ACCOUNT_SID / AUTH_TOKEN / FROM_NUMBER to go live"}
                </Badge>
              </div>
              <Table>
                <THead>
                  <TR><TH>Sent</TH><TH>To</TH><TH>Provider</TH><TH>Status</TH><TH>Body</TH></TR>
                </THead>
                <TBody>
                  {smsLog.map((s) => (
                    <TR key={s.id} testid={`sms-row-${s.id}`}>
                      <TD>{fmt(s.sent_at)}</TD>
                      <TD>{s.to_number || <span className="text-muted-foreground">—</span>}</TD>
                      <TD><Badge variant={s.provider === "twilio" ? "default" : "secondary"}>{s.provider}</Badge></TD>
                      <TD>
                        <Badge variant={s.status?.startsWith("skipped") || s.status === "failed" ? "secondary" : "default"}>
                          {s.status}
                        </Badge>
                      </TD>
                      <TD className="max-w-[520px] text-xs">{s.body}</TD>
                    </TR>
                  ))}
                  {smsLog.length === 0 && <TR><TD colSpan={5}><Empty>No SMS yet. Click "Force send now" to demo.</Empty></TD></TR>}
                </TBody>
              </Table>
            </Section>
          </TabsContent>

          <TabsContent value="applicants">
            <Section
              title="Applicants"
              description="Careers portal · view submitted applications, download encrypted documents, and unlock training."
              testid="section-applicants"
            >
              <Table>
                <THead>
                  <TR><TH>Submitted</TH><TH>Name</TH><TH>Email</TH><TH>Status</TH><TH>SSN</TH><TH>Docs</TH><TH></TH></TR>
                </THead>
                <TBody>
                  {applicants.map((a) => {
                    const fileCount = Object.keys(a.file_ids || {}).length;
                    return (
                      <TR key={a.id} testid={`applicant-row-${a.email}`}>
                        <TD>{a.submitted_at ? fmt(a.submitted_at) : <span className="text-muted-foreground">draft</span>}</TD>
                        <TD className="font-medium text-foreground">{a.first_name} {a.last_name}</TD>
                        <TD>{a.email}</TD>
                        <TD><Badge variant={a.status === "hired" || a.status.startsWith("training") || a.status === "pre_screened" ? "default" : "secondary"}>{a.status.replace(/_/g, " ")}</Badge></TD>
                        <TD className="font-mono text-xs">{a.ssn_masked || "—"}</TD>
                        <TD>{fileCount}</TD>
                        <TD>
                          <Button size="sm" variant="ghost" data-testid={`btn-open-applicant-${a.email}`} onClick={() => openApplicant(a.id)}>
                            Open <ExternalLink size={12} className="ml-1" />
                          </Button>
                        </TD>
                      </TR>
                    );
                  })}
                  {applicants.length === 0 && <TR><TD colSpan={7}><Empty>No applicants yet.</Empty></TD></TR>}
                </TBody>
              </Table>
            </Section>
          </TabsContent>

          <TabsContent value="guests">
            <GuestInvitesTab events={events} onSent={loadAll} />
          </TabsContent>

          <TabsContent value="payments">
            <PaymentsTab />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab />
          </TabsContent>

          <TabsContent value="scheduler">
            <Section
              title="Pre-event automation"
              description="Runs every 10 minutes. Sends 'Action Required' if profile incomplete, or 'Express Pass' if complete."
              action={
                <Button onClick={runScheduler} disabled={busy} className="rounded-full bg-primary text-primary-foreground" data-testid="btn-run-scheduler-2">
                  <RefreshCcw size={14} className="mr-1.5" />
                  {busy ? "Running…" : "Run now"}
                </Button>
              }
            >
              <Table>
                <THead>
                  <TR><TH>Ran at</TH><TH>Events in window</TH><TH>Processed</TH><TH>Sent</TH><TH>Skipped</TH><TH>Variants</TH></TR>
                </THead>
                <TBody>
                  {runs.map((r, i) => (
                    <TR key={i}>
                      <TD>{fmt(r.ran_at)}</TD>
                      <TD>{r.events_in_window}</TD>
                      <TD>{r.bookings_processed}</TD>
                      <TD>{r.emails_sent}</TD>
                      <TD>{r.skipped_duplicates_or_missing}</TD>
                      <TD className="text-xs">{r.variants && JSON.stringify(r.variants)}</TD>
                    </TR>
                  ))}
                  {runs.length === 0 && <TR><TD colSpan={6}><Empty>No job runs yet.</Empty></TD></TR>}
                </TBody>
              </Table>
            </Section>
          </TabsContent>
        </Tabs>
      </div>

      {/* Email preview dialog */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display">{preview?.subject}</DialogTitle>
            <DialogDescription asChild>
              <div className="flex items-center gap-2 flex-wrap">
                <span>To: {preview?.to_email} · Variant:</span>
                <Badge variant={preview?.variant === "express_pass" ? "default" : "secondary"}>
                  {preview?.variant?.replace("_", " ")}
                </Badge>
              </div>
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="flex-1 overflow-auto border border-border rounded-lg bg-muted/50">
              <iframe
                title="email-preview"
                data-testid="email-preview-iframe"
                srcDoc={preview.body_html}
                className="w-full h-[520px] bg-white"
              />
            </div>
          )}
          {preview?.magic_link && (
            <div className="text-xs text-muted-foreground break-all">
              Magic link: <a href={preview.magic_link} target="_blank" rel="noreferrer" className="text-primary underline">{preview.magic_link}</a>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Applicant detail dialog */}
      <Dialog open={!!selectedApplicant} onOpenChange={(v) => !v && setSelectedApplicant(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="applicant-detail-dialog">
          {selectedApplicant && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif-display">{selectedApplicant.first_name} {selectedApplicant.last_name}</DialogTitle>
                <DialogDescription>
                  <span>{selectedApplicant.email} · {selectedApplicant.phone}</span>
                </DialogDescription>
                <div className="pt-1">
                  <Badge>{selectedApplicant.status.replace(/_/g, " ")}</Badge>
                </div>
              </DialogHeader>
              <div className="space-y-5 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Behavioral</div>
                  <div className="rounded-lg bg-muted/40 p-3 space-y-2 text-xs">
                    <div><strong>Q1:</strong> {selectedApplicant.behavioral?.q1_high_profile || "—"}</div>
                    <div><strong>Q2:</strong> {selectedApplicant.behavioral?.q2_distressed_child || "—"}</div>
                    <div><strong>Q3:</strong> {selectedApplicant.behavioral?.q3_care_philosophy || "—"}</div>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Work history</div>
                  <div className="space-y-2 text-xs">
                    {(selectedApplicant.work_history || []).map((w, i) => (
                      <div key={i} className="rounded-lg bg-muted/40 p-3">
                        <div className="font-medium text-foreground">{w.company} · {w.supervisor}</div>
                        <div className="text-muted-foreground">{w.dates_start} → {w.dates_end || "present"} · {w.contact}</div>
                        <div className="mt-1">{w.responsibilities}</div>
                      </div>
                    ))}
                    {(!selectedApplicant.work_history || selectedApplicant.work_history.length === 0) && <div className="text-muted-foreground">No entries</div>}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Sensitive · encrypted at rest</div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="font-mono">SSN: {selectedApplicant.ssn_masked || "—"}</span>
                    {selectedApplicant.ssn_masked && (
                      <Button size="sm" variant="outline" data-testid="btn-reveal-ssn" onClick={() => revealSSN(selectedApplicant.id)} className="rounded-full">Reveal (audited)</Button>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Documents (encrypted)</div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(selectedApplicant.file_ids || {}).map(([kind, fid]) => (
                      <Button key={kind} variant="outline" size="sm" data-testid={`btn-download-${kind}`} onClick={() => downloadFile(selectedApplicant.id, fid, kind)} className="rounded-lg justify-start">
                        <ExternalLink size={12} className="mr-1.5" /> {kind}
                      </Button>
                    ))}
                    {Object.keys(selectedApplicant.file_ids || {}).length === 0 && <div className="text-xs text-muted-foreground">No documents uploaded.</div>}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Applied for role</div>
                  <Badge variant="secondary" data-testid="applicant-applied-role">{(selectedApplicant.applied_role || "caregiver").replace(/_/g, " ")}</Badge>
                </div>
                <CharacterReviewPanel applicantId={selectedApplicant.id} />
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Change status</div>
                  <div className="flex flex-wrap gap-2">
                    {["in_review", "pre_screened", "training_complete", "second_interview", "hired", "rejected"].map((s) => (
                      <Button key={s} size="sm" data-testid={`btn-status-${s}`} onClick={() => updateApplicantStatus(selectedApplicant.id, s)} variant={selectedApplicant.status === s ? "default" : "outline"} className="rounded-full">
                        {s.replace(/_/g, " ")}
                      </Button>
                    ))}
                  </div>
                  <div className="mt-2 text-[11px] text-muted-foreground">Moving to <strong>second_interview</strong> or <strong>hired</strong> triggers an automated scheduling email (if the URL is configured in Settings).</div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

// --- tiny table primitives (kept local, no new files) ---
const Table = ({ children }) => (
  <div className="overflow-x-auto"><table className="w-full text-sm">{children}</table></div>
);
const THead = ({ children }) => <thead className="text-xs uppercase tracking-widest text-muted-foreground">{children}</thead>;
const TBody = ({ children }) => <tbody className="divide-y divide-border">{children}</tbody>;
const TR = ({ children, testid, onClick, className = "" }) => (
  <tr data-testid={testid} onClick={onClick} className={`${className}`}>{children}</tr>
);
const TH = ({ children }) => <th className="text-left font-medium py-3 px-2">{children}</th>;
const TD = ({ children, className = "", colSpan }) => (
  <td colSpan={colSpan} className={`py-3 px-2 align-middle text-foreground/90 ${className}`}>{children}</td>
);
const Empty = ({ children }) => <div className="py-10 text-center text-muted-foreground text-sm">{children}</div>;

// --- Create Event Form ---
const CreateEventForm = ({ onCreated }) => {
  const [form, setForm] = React.useState({
    name: "", slug: "", venue: "", start_at: "", end_at: "", description: "",
  });
  const [busy, setBusy] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug || !form.venue || !form.start_at) {
      toast.error("Name, slug, venue, and start date are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
      };
      await api.post("/admin/events", payload);
      toast.success("Event created");
      setForm({ name: "", slug: "", venue: "", start_at: "", end_at: "", description: "" });
      onCreated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="new-event-form"
      className="rounded-2xl border border-border/70 bg-card p-6 h-fit"
    >
      <div className="text-xs uppercase tracking-widest text-primary/80">Create event</div>
      <h3 className="font-serif-display text-xl mt-1 mb-4">New event</h3>
      <div className="space-y-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name</Label>
          <Input data-testid="event-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-10 rounded-lg" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Slug (URL)</Label>
          <Input data-testid="event-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="gala2026" className="mt-1.5 h-10 rounded-lg" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Venue</Label>
          <Input data-testid="event-venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="mt-1.5 h-10 rounded-lg" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Start</Label>
          <Input type="datetime-local" data-testid="event-start" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="mt-1.5 h-10 rounded-lg" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">End (optional)</Label>
          <Input type="datetime-local" data-testid="event-end" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} className="mt-1.5 h-10 rounded-lg" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label>
          <Textarea data-testid="event-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 rounded-lg resize-none" />
        </div>
      </div>
      <Button
        type="submit"
        data-testid="create-event-btn"
        disabled={busy}
        className="mt-5 w-full rounded-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground"
      >
        {busy ? "Creating…" : "Create event"}
      </Button>
    </form>
  );
};

// -----------------------------------------------------------------------------
// Guest Invites (Gmail SMTP) tab
// -----------------------------------------------------------------------------
const GuestInvitesTab = ({ events, onSent }) => {
  const [eventId, setEventId] = useState(events[0]?.id || "");
  const [raw, setRaw] = useState("Alex Chen, alex@example.com\nJordan Lee, jordan@example.com");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [gmailReady, setGmailReady] = useState(false);

  useEffect(() => {
    api.get("/public/config").then(({ data }) => setGmailReady(!!data.gmail_ready)).catch(() => {});
  }, []);

  const parseGuests = (text) => {
    const guests = [];
    for (const line of text.split(/\r?\n/)) {
      const s = line.trim();
      if (!s) continue;
      const parts = s.split(/[,;]/).map((x) => x.trim()).filter(Boolean);
      let name = "", email = "";
      for (const p of parts) {
        if (p.includes("@")) email = p;
        else if (!name) name = p;
      }
      if (email) guests.push({ email, name: name || email.split("@")[0] });
    }
    return guests;
  };

  const send = async () => {
    if (!eventId) { toast.error("Choose an event"); return; }
    const guests = parseGuests(raw);
    if (!guests.length) { toast.error("Add at least one guest (name, email per line)"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/admin/events/${eventId}/guest-invites`, { guests });
      setResult(data);
      toast.success(`Sent ${data.sent} / ${data.total} invites (${data.gmail_ready ? "Gmail" : "mocked — no App Password"})`);
      onSent?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Send failed");
    } finally { setBusy(false); }
  };

  const guests = parseGuests(raw);

  return (
    <Section
      title="Guest-list invites"
      description="Paste one guest per line as `Name, email@example.com`. Each guest gets a unique invite link + QR embedded in the branded email."
      testid="section-guest-invites"
    >
      <div className="mb-4 text-xs">
        Gmail delivery:{" "}
        <Badge variant={gmailReady ? "default" : "secondary"} data-testid="gmail-status">
          {gmailReady ? "LIVE (careanywhereagency@gmail.com)" : "Waiting for GMAIL_APP_PASSWORD — sends logged only"}
        </Badge>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Event</Label>
            <select
              data-testid="guest-invite-event"
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              className="mt-1.5 w-full h-10 rounded-lg border border-border bg-background px-3 text-sm"
            >
              <option value="">— select an event —</option>
              {events.map((e) => <option key={e.id} value={e.id}>{e.name} · {new Date(e.start_at).toLocaleDateString()}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Guest list (Name, email — one per line)</Label>
            <Textarea data-testid="guest-invite-list" rows={10} value={raw} onChange={(e) => setRaw(e.target.value)} className="mt-1.5 font-mono text-xs rounded-lg" />
            <div className="mt-1 text-xs text-muted-foreground">{guests.length} valid guest{guests.length === 1 ? "" : "s"} parsed.</div>
          </div>
          <div className="flex justify-end">
            <Button data-testid="btn-send-guest-invites" disabled={busy || !eventId || !guests.length} onClick={send} className="rounded-full bg-primary text-primary-foreground">
              {busy ? "Sending…" : `Send ${guests.length || ""} invite${guests.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-border/70 bg-muted/40 p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Latest send</div>
            {!result ? (
              <div className="text-sm text-muted-foreground">No sends yet.</div>
            ) : (
              <div className="space-y-1.5 text-sm">
                <div>Total: <span className="font-medium">{result.total}</span></div>
                <div>Delivered: <span className="font-medium">{result.sent}</span></div>
                <div className="text-xs text-muted-foreground">Provider: {result.gmail_ready ? "Gmail" : "mock"}</div>
                <div className="max-h-56 overflow-auto mt-2 space-y-1 text-xs">
                  {(result.results || []).map((r, i) => (
                    <div key={i} className="border-b border-border/50 py-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-medium">{r.email}</span>
                        <Badge variant={r.ok ? "default" : "secondary"} className="text-[10px]">{r.status}</Badge>
                      </div>
                      {r.url && (
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText(r.url); toast.success("Invite URL copied"); }}
                          className="mt-1 block w-full text-left text-[10px] text-primary/80 hover:text-primary truncate"
                          data-testid={`copy-invite-${i}`}
                          title="Click to copy"
                        >
                          {r.url}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
};

// -----------------------------------------------------------------------------
// Payments tab — PayPal + Stripe combined
// -----------------------------------------------------------------------------
const PaymentsTab = () => {
  const [pp, setPp] = useState([]);
  const [stripe, setStripe] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        api.get("/admin/payments"),          // PayPal
        api.get("/admin/stripe/transactions"),
      ]);
      setPp(p);
      setStripe(s);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const refund = async (sid) => {
    if (!window.confirm("Full refund this Stripe payment?")) return;
    try {
      await api.post("/admin/stripe/refund", { session_id: sid });
      toast.success("Refund issued");
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Refund failed");
    }
  };

  const capturedPP = pp.filter((r) => r.kind === "order_captured");
  const paidStripe = stripe.filter((r) => r.payment_status === "paid");
  const totalPP = capturedPP.reduce((s, r) => s + (Number(r.amount_usd) || 0), 0);
  const totalStripe = paidStripe.reduce((s, r) => s + (Number(r.amount || 0) / 100), 0);

  return (
    <Section title="Payments" description="Stripe + PayPal transactions." testid="section-payments">
      <div className="mb-4 flex flex-wrap gap-2 text-xs">
        <Badge variant="default">Stripe captured: {paidStripe.length} · ${totalStripe.toFixed(2)}</Badge>
        <Badge variant="secondary">PayPal captured: {capturedPP.length} · ${totalPP.toFixed(2)}</Badge>
      </div>

      {loading ? (
        <Empty>Loading…</Empty>
      ) : (
        <>
          <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">Stripe</div>
          <Table>
            <THead>
              <TR><TH>At</TH><TH>Purpose</TH><TH>Amount</TH><TH>Status</TH><TH>Booking</TH><TH>Session</TH><TH>Action</TH></TR>
            </THead>
            <TBody>
              {stripe.map((r) => (
                <TR key={r.session_id} testid={`stripe-row-${r.session_id}`}>
                  <TD>{fmt(r.created_at)}</TD>
                  <TD>{r.purpose || "—"}</TD>
                  <TD>${(Number(r.amount || 0) / 100).toFixed(2)}</TD>
                  <TD><Badge variant={r.payment_status === "paid" ? "default" : "secondary"} className="text-[10px]">{r.payment_status}</Badge></TD>
                  <TD className="font-mono text-xs">{r.booking_id || "—"}</TD>
                  <TD className="font-mono text-xs">{r.session_id.slice(0, 22)}…</TD>
                  <TD>
                    {r.payment_status === "paid" && (
                      <Button size="sm" variant="outline" onClick={() => refund(r.session_id)} className="rounded-full h-7 text-[11px]" data-testid={`btn-refund-${r.session_id}`}>Refund</Button>
                    )}
                  </TD>
                </TR>
              ))}
              {stripe.length === 0 && <TR><TD colSpan={7}><Empty>No Stripe transactions yet.</Empty></TD></TR>}
            </TBody>
          </Table>

          <div className="mt-8 mb-3 text-xs uppercase tracking-widest text-muted-foreground">PayPal</div>
          <Table>
            <THead>
              <TR><TH>At</TH><TH>Kind</TH><TH>Purpose</TH><TH>Amount</TH><TH>Booking</TH><TH>Order ID</TH><TH>Status</TH></TR>
            </THead>
            <TBody>
              {pp.map((r) => (
                <TR key={r.id + r.kind + r.at} testid={`payment-row-${r.id}`}>
                  <TD>{fmt(r.at)}</TD>
                  <TD><Badge variant={r.kind === "order_captured" ? "default" : "secondary"} className="text-[10px]">{r.kind}</Badge></TD>
                  <TD>{r.purpose || "—"}</TD>
                  <TD>${Number(r.amount_usd || 0).toFixed(2)}</TD>
                  <TD className="font-mono text-xs">{r.booking_id || "—"}</TD>
                  <TD className="font-mono text-xs">{r.id}</TD>
                  <TD><Badge variant="secondary" className="text-[10px]">{r.status}</Badge></TD>
                </TR>
              ))}
              {pp.length === 0 && <TR><TD colSpan={7}><Empty>No PayPal transactions yet.</Empty></TD></TR>}
            </TBody>
          </Table>
        </>
      )}
    </Section>
  );
};

// -----------------------------------------------------------------------------
// Character Review Panel — integrity flags per applicant
// -----------------------------------------------------------------------------
const CharacterReviewPanel = ({ applicantId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/applicants/${applicantId}/character-review`).then(({ data }) => {
      if (alive) setData(data);
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [applicantId]);

  if (loading) return null;
  const total = (data?.counts?.high || 0) + (data?.counts?.medium || 0) + (data?.counts?.low || 0);
  if (!total) return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Character review</div>
      <Badge variant="secondary" data-testid="char-review-clean" className="text-[10px]">No flags — all integrity items answered in expected range.</Badge>
    </div>
  );
  const sevColor = data.max_severity === "high" ? "bg-destructive text-destructive-foreground"
    : data.max_severity === "medium" ? "bg-amber-500 text-white"
    : "bg-yellow-300 text-yellow-950";

  return (
    <div data-testid="char-review-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Character review</div>
        <button
          type="button"
          data-testid="btn-char-review-toggle"
          onClick={() => setOpen((v) => !v)}
          className="text-[11px] text-primary hover:underline"
        >
          {open ? "Hide details" : "View details"}
        </button>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <span data-testid="char-review-severity" className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${sevColor}`}>
          {(data.max_severity || "").toUpperCase()} · {total} flag{total === 1 ? "" : "s"}
        </span>
        <Badge variant="secondary" className="text-[10px]">High: {data.counts.high}</Badge>
        <Badge variant="secondary" className="text-[10px]">Medium: {data.counts.medium}</Badge>
        <Badge variant="secondary" className="text-[10px]">Low: {data.counts.low}</Badge>
      </div>
      {open && (
        <div data-testid="char-review-details" className="mt-3 space-y-3 max-h-72 overflow-y-auto rounded-lg border border-border/70 bg-muted/40 p-3">
          {(data.flags || []).map((row, idx) => (
            <div key={idx} className="text-xs">
              <div className="font-medium">{row.module_title}</div>
              {(row.flags || []).map((f, fi) => (
                <div key={fi} className="mt-1 pl-2 border-l-2 border-primary/40">
                  <div className="text-muted-foreground italic">"{f.prompt}"</div>
                  <div className="mt-0.5">
                    Chose: <span className="font-medium">{f.chosen_text}</span>{" "}
                    <Badge variant="secondary" className="ml-1 text-[9px]">{f.severity}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 text-[10px] text-muted-foreground">Flags are advisory. Use with your HR review process — not as a sole rejection criterion.</div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Settings tab — interview scheduling URL, etc.
// -----------------------------------------------------------------------------
const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      setSettings(data);
      setUrl(data.interview_scheduling_url || "");
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/admin/settings", { interview_scheduling_url: url.trim() });
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Section
      title="Global settings"
      description="Configure the scheduling link that gets emailed to applicants moved to 'second interview' or 'hired'."
      testid="section-settings"
    >
      <div className="max-w-2xl space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">2nd-interview scheduling URL</Label>
          <Input
            data-testid="input-scheduling-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://calendly.com/careanywhere/second-interview"
            className="mt-1.5 h-11 rounded-lg"
          />
          <div className="mt-1.5 text-xs text-muted-foreground">
            Paste your Calendly, Cal.com, or Google Calendar booking URL. Leave blank to disable auto-email.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button data-testid="btn-save-settings" onClick={save} disabled={busy} className="rounded-full bg-primary text-primary-foreground">
            {busy ? "Saving…" : "Save settings"}
          </Button>
          {settings?.updated_at && (
            <span className="text-xs text-muted-foreground">Last saved {new Date(settings.updated_at).toLocaleString()}</span>
          )}
        </div>
      </div>
    </Section>
  );
};

export default AdminDashboard;
